import { Box, Flex, Text } from "@chakra-ui/react";
import { Tooltip } from "antd";
import useBloqueioDatasAreaComum from "core/features/area-comum/hooks/useBloqueioDatasAreaComum";
import useSaveBloqueioDataAreaComum from "core/features/area-comum/hooks/useSaveBloqueioDataAreaComum";
import {
  AreaComumBloqueioDataDiaItem,
  AreaComumBloqueioDataItem,
} from "core/features/area-comum/typings";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import { showConfirm, showErrorAxios, showSuccess } from "infra/helpers/alerta";
import moment from "moment";
import { useEffect, useState } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import OutlineButton from "ui/components/Button/OutlineButton";

import PrimaryButton from "ui/components/Button/PrimaryButton";
import NewTable from "ui/components/NewTable";
import CustomTag from "ui/components/Tags/CustomTag";
import { weekdays } from "../RangeDates";
import ModalBloqueioData from "./ModalBloqueioData";

function BloqueioDatas({ id }: { id: string }) {
  const { tenant } = useCurrentTenant();

  const [openModal, setOpenModal] = useState(false);
  const [editItem, setEditItem] = useState<AreaComumBloqueioDataItem>();
  const [items, setItems] = useState<AreaComumBloqueioDataItem[]>([]);

  const { data, isFetching } = useBloqueioDatasAreaComum(tenant.id, id);
  const { mutate: handleSave, isLoading } = useSaveBloqueioDataAreaComum(
    tenant.id,
    id
  );

  useEffect(() => {
    setItems(data ?? []);
  }, [data]);

  const _handleSave = (values: any) => {
    handleSave(values, {
      onError: (err) => {
        showErrorAxios(err);
      },
      onSuccess: () => {
        showSuccess();
      },
    });
  };

  const formatTime = (item: AreaComumBloqueioDataDiaItem) => {
    const inicio = `${item.horaInicio?.split(":").slice(0, 2).join(":")}`;

    if (item.horaFim) {
      const fim = `${item.horaFim.split(":").slice(0, 2).join(":")}`;

      return `${inicio} até as ${fim}`;
    }

    return inicio;
  };

  return (
    <>
      <Flex>
        <Flex flex={1}>
          <Text mr="16px" fontSize={"18px"} fontWeight={"bold"} mb="32px">
            Datas Bloqueadas
          </Text>
          <OutlineButton onClick={() => setOpenModal(true)} size="xs">
            Adicionar bloqueio
          </OutlineButton>
        </Flex>
        <Flex>
          <PrimaryButton
            isDisabled={isLoading}
            isLoading={isLoading}
            onClick={() => _handleSave(items)}
          >
            Salvar
          </PrimaryButton>
        </Flex>
      </Flex>

      <Box border="1px solid #eee" rounded={"16px"}>
        <NewTable
          hideShadow
          loading={isFetching}
          dataSource={items}
          size="small"
          actions={[
            {
              title: "Alterar",
              icon: () => <MdEdit />,
              onClick: (v) => {
                setEditItem(v);
                setOpenModal(true);
              },
            },
            {
              title: "Remover",
              icon: () => <MdDelete />,
              onClick: (v) => {
                showConfirm().then(({ isConfirmed }) => {
                  if (isConfirmed) {
                    const newItems = items.filter((p) => p !== v);

                    setItems(newItems);
                  }
                });
              },
            },
          ]}
          columns={[
            {
              title: "Data",
              render: (item: AreaComumBloqueioDataItem) => {
                if (item.dataFim) {
                  return (
                    <Box w="200px">
                      {moment(item.dataInicio).format("L")} até{" "}
                      {moment(item.dataFim).format("L")}
                    </Box>
                  );
                }

                return moment(item.dataInicio).format("L");
              },
            },
            {
              title: "Dias da semana",
              render: (item: AreaComumBloqueioDataItem) => (
                <Flex gap="8px" flexWrap={"wrap"}>
                  {Array.isArray(item.items)
                    ? item.items.map((subitem, key) => (
                        <Tooltip
                          key={key}
                          placement="bottom"
                          title={`${formatTime(subitem)}`}
                        >
                          <CustomTag color="blue.600">
                            {weekdays[subitem.dia]}
                          </CustomTag>
                        </Tooltip>
                      ))
                    : null}

                  {!item.items?.length ? (
                    <CustomTag color="blue.600">
                      {moment(item.dataInicio).format("dddd")}
                    </CustomTag>
                  ) : null}
                </Flex>
              ),
            },
            {
              title: "Motivo",
              width: "500px",
              render: (item: AreaComumBloqueioDataItem) => {
                return item.motivo ? item.motivo : "--";
              },
            },
          ]}
        />
      </Box>

      {openModal ? (
        <ModalBloqueioData
          open={openModal}
          defaultData={editItem}
          onClose={() => {
            setOpenModal(false);
            setEditItem(undefined);
          }}
          onSaved={(v) => {
            setItems((prev) => {
              const exists = prev.findIndex((p) => p.id === v.id);

              if (exists === -1) {
                return [...prev, v];
              }

              prev[exists] = { ...v };

              return [...prev];
            });
            setOpenModal(false);

            setEditItem(undefined);
          }}
        />
      ) : null}
    </>
  );
}

export default BloqueioDatas;
